// ListaCompras — pagina /lista-compras.
// Lista de produtos ativos com flag de cotacoes abertas. Botao "Enviar para Cotacao" abre
// modal com cotacoes abertas (expand mostra itens). Backend ja existe (lista-compras.routes.ts).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import {
  ArrowDown,
  ArrowUp,
  Filter,
  ImageOff,
  Loader2,
  Minus,
  MoreVertical,
  Plus,
  Search,
  Send,
  ShoppingCart,
  X,
} from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { FotoProduto } from '../../components/FotoProduto';
import { textoQtdComprada } from '../../components/SeloChegando';
import { formatMoeda, formatDataSP } from '@fiodecobre/shared';

interface Produto {
  id: number;
  codigo_interno: string;
  descricao: string;
  foto_url: string | null;
  marca_nome: string | null;
  unidade_sigla: string | null;
  grupo_nome: string | null;
  preco_cartao: string | null;
  estoque_atual: string;
  estoque_minimo: string;
  localizacao: string | null;
  cotacoes_abertas?: string[];
  // Compra em aberto (DA-368): comprado, a caminho, ainda sem credito de estoque.
  compra_em_aberto: boolean;
  compra_em_aberto_qtd: string | null;
  compra_em_aberto_emb: string | null;
  ultima_compra_em: string | null;
  ultima_compra_fornecedor_nome: string | null;
}

interface Auxiliar {
  id: number;
  nome: string;
}

interface CotacaoAberta {
  id: number;
  numero: string;
  observacoes: string | null;
  total_itens: string;
}

interface ItemCotacao {
  id: number;
  codigo_interno: string;
  descricao: string;
  quantidade: string;
  unidade_sigla: string | null;
}

export function ListaCompras() {
  const toast = useToast();
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState<{ grupo_id: string; marca_id: string; categoria_id: string }>({
    grupo_id: '',
    marca_id: '',
    categoria_id: '',
  });
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [grupos, setGrupos] = useState<Auxiliar[]>([]);
  const [marcas, setMarcas] = useState<Auxiliar[]>([]);
  const [categorias, setCategorias] = useState<Auxiliar[]>([]);
  const [ordenEstoque, setOrdenEstoque] = useState<'asc' | 'desc' | null>(null);
  const [excluirFracionados, setExcluirFracionados] = useState(false);
  const [soEstoqueMinimo, setSoEstoqueMinimo] = useState(false);
  // "Mostrar a caminho" (DA-368): default DESMARCADO — produtos com compra em aberto ficam
  // ocultos (ja foram comprados, estao a caminho, nao precisam entrar de novo na lista).
  const [mostrarACaminho, setMostrarACaminho] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [menuAberto, setMenuAberto] = useState<{ id: number; descricao: string; x: number; y: number } | null>(null);
  const [modalCotacao, setModalCotacao] = useState<{ produto_id: number; descricao: string } | null>(null);
  const rowRefs = useRef<Array<HTMLTableRowElement | null>>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Carregar auxiliares
  useEffect(() => {
    Promise.all([
      api.get<Auxiliar[]>('/api/grupos').catch(() => [] as Auxiliar[]),
      api.get<Auxiliar[]>('/api/marcas').catch(() => [] as Auxiliar[]),
      api.get<Auxiliar[]>('/api/categorias').catch(() => [] as Auxiliar[]),
    ]).then(([g, m, c]) => {
      setGrupos(g);
      setMarcas(m);
      setCategorias(c);
    });
  }, []);

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (busca) params.q = busca;
      if (filtros.grupo_id) params.grupo_id = filtros.grupo_id;
      if (filtros.marca_id) params.marca_id = filtros.marca_id;
      if (filtros.categoria_id) params.categoria_id = filtros.categoria_id;
      if (excluirFracionados) params.excluir_fracionados = '1';
      if (mostrarACaminho) params.incluir_a_caminho = '1';
      const data = await api.get<Produto[]>('/api/lista-compras', { query: params });
      setProdutos(data);
      setHighlightIdx(-1);
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao carregar lista de compras');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, filtros, excluirFracionados, mostrarACaminho]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(carregar, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [carregar]);

  const temFiltrosAtivos = !!(filtros.grupo_id || filtros.marca_id || filtros.categoria_id);

  function toggleOrdenEstoque() {
    setOrdenEstoque((prev) => (prev === null ? 'asc' : prev === 'asc' ? 'desc' : null));
  }

  // Filtro "Estoque Minimo": so produtos com Est. Atual <= Estoque Minimo
  // (mesma condicao que ja pinta o estoque de vermelho na tabela).
  const produtosFiltrados = useMemo(() => {
    if (!soEstoqueMinimo) return produtos;
    return produtos.filter((p) => Number(p.estoque_atual) <= Number(p.estoque_minimo));
  }, [produtos, soEstoqueMinimo]);

  const produtosOrdenados = useMemo(() => {
    if (!ordenEstoque) return produtosFiltrados;
    return [...produtosFiltrados].sort((a, b) => {
      const diff = Number(a.estoque_atual) - Number(b.estoque_atual);
      return ordenEstoque === 'asc' ? diff : -diff;
    });
  }, [produtosFiltrados, ordenEstoque]);

  // Navegacao por teclado
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.defaultPrevented || modalCotacao) return;
      const len = produtosOrdenados.length;
      if (!len) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIdx((prev) => {
          const next = Math.min(prev + 1, len - 1);
          rowRefs.current[next]?.scrollIntoView({ block: 'nearest' });
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIdx((prev) => {
          const next = Math.max(prev - 1, 0);
          rowRefs.current[next]?.scrollIntoView({ block: 'nearest' });
          return next;
        });
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [produtosOrdenados.length, modalCotacao]);

  // Fechar menu ao clicar fora
  useEffect(() => {
    if (!menuAberto) return;
    const handler = () => setMenuAberto(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [menuAberto]);

  function abrirMenu(e: React.MouseEvent, p: Produto) {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const flipUp = rect.bottom + 60 > window.innerHeight;
    setMenuAberto({
      id: p.id,
      descricao: p.descricao,
      x: rect.left - 160,
      y: flipUp ? rect.top - 40 : rect.bottom + 4,
    });
  }

  const sticky = useStickyHeader();

  return (
    <div className="modulo-pagina">
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo">
            <ShoppingCart size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
            Lista de Compras
          </h2>
        </div>
      </div>

      <div style={{ padding: '12px 16px 16px' }}>
      {/* Busca + filtros */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="text"
              placeholder="Buscar por descricao, codigo, marca ou grupo..."
              value={busca}
              onChange={(e) => setBusca(upperMantendoCursor(e))}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                border: '1px solid var(--border)',
                borderRadius: 8,
                fontSize: 13,
                background: 'var(--input-bg)',
                color: 'var(--text-color)',
              }}
            />
          </div>
          <button
            type="button"
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              border: '1px solid',
              borderColor: temFiltrosAtivos ? 'var(--primary)' : 'var(--input-border)',
              background: temFiltrosAtivos ? 'var(--primary-soft)' : 'var(--input-bg)',
              color: temFiltrosAtivos ? 'var(--primary)' : 'var(--text-muted)',
              borderRadius: 8,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            <Filter size={15} /> Filtros
          </button>
        </div>

        {mostrarFiltros && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              padding: 12,
              background: 'var(--grid-row-alt-bg)',
              borderRadius: 8,
              border: '1px solid var(--border)',
            }}
          >
            <select
              value={filtros.grupo_id}
              onChange={(e) => setFiltros({ ...filtros, grupo_id: e.target.value })}
              style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }}
            >
              <option value="">Todos grupos</option>
              {grupos.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.nome}
                </option>
              ))}
            </select>
            <select
              value={filtros.marca_id}
              onChange={(e) => setFiltros({ ...filtros, marca_id: e.target.value })}
              style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }}
            >
              <option value="">Todas marcas</option>
              {marcas.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nome}
                </option>
              ))}
            </select>
            <select
              value={filtros.categoria_id}
              onChange={(e) => setFiltros({ ...filtros, categoria_id: e.target.value })}
              style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }}
            >
              <option value="">Todas categorias</option>
              {categorias.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
            {temFiltrosAtivos && (
              <button
                type="button"
                onClick={() => setFiltros({ grupo_id: '', marca_id: '', categoria_id: '' })}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '6px 12px',
                  fontSize: 13,
                  color: '#dc2626',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                <X size={14} /> Limpar
              </button>
            )}
          </div>
        )}
      </div>

      {/* Contador + checkbox */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, fontSize: 13, color: 'var(--text-muted)' }}>
        <div>
          {produtosOrdenados.length} produto{produtosOrdenados.length !== 1 ? 's' : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
            title="Mostrar apenas produtos com Est. Atual menor ou igual ao Minimo de Compra (precisam ser comprados)"
          >
            <input
              type="checkbox"
              checked={soEstoqueMinimo}
              onChange={(e) => setSoEstoqueMinimo(e.target.checked)}
            />
            Mín. Compra
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={excluirFracionados}
              onChange={(e) => setExcluirFracionados(e.target.checked)}
            />
            Excluir Fracionamento
          </label>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
            title="Produtos ja comprados (compra em aberto) ficam ocultos por padrao — ja estao a caminho"
          >
            <input
              type="checkbox"
              checked={mostrarACaminho}
              onChange={(e) => setMostrarACaminho(e.target.checked)}
            />
            Mostrar a caminho
          </label>
        </div>
      </div>

      {/* Tabela */}
      <div style={{ background: 'var(--card-bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
        ) : produtosOrdenados.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
            <ShoppingCart size={40} style={{ margin: '0 auto 8px', display: 'block', color: 'var(--border)' }} />
            Nenhum produto encontrado
          </div>
        ) : (
          <div>
            <table style={{ width: '100%', fontSize: 13, ...sticky.tableStyle }}>
              <thead>
                <tr>
                  <th style={{ ...sticky.thStyle, padding: '8px', textAlign: 'center', width: 56, fontWeight: 600 }}>Foto</th>
                  <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'left', width: 64, fontWeight: 600 }}>Cod.</th>
                  <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Descricao</th>
                  <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Marca</th>
                  <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'center', width: 64, fontWeight: 600 }}>Un.</th>
                  <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Grupo</th>
                  <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'right', width: 96, fontWeight: 600 }}>Pr Cartao</th>
                  <th
                    style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'right', width: 80, fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                    onClick={toggleOrdenEstoque}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                      Estoque
                      {ordenEstoque === 'asc' && <ArrowUp size={13} />}
                      {ordenEstoque === 'desc' && <ArrowDown size={13} />}
                    </span>
                  </th>
                  <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'right', width: 80, fontWeight: 600 }} title="Abaixo disso o produto entra na Lista de Compras">Mín. Compra</th>
                  <th style={{ ...sticky.thStyle, padding: '8px 12px', textAlign: 'left', width: 96, fontWeight: 600 }}>Local</th>
                  <th style={{ ...sticky.thStyle, width: 36 }}></th>
                </tr>
              </thead>
              <tbody>
                {produtosOrdenados.map((p, idx) => {
                  const estoqueAlerta = Number(p.estoque_atual) <= Number(p.estoque_minimo);
                  return (
                    <tr
                      key={p.id}
                      ref={(el) => {
                        rowRefs.current[idx] = el;
                      }}
                      style={{
                        borderBottom: '1px solid var(--border)',
                        cursor: 'pointer',
                        background: idx === highlightIdx ? 'var(--grid-row-hover-bg, #dbeafe)' : undefined,
                      }}
                      onClick={() => setHighlightIdx(idx)}
                    >
                      <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                        {p.foto_url ? (
                          <FotoProduto url={p.foto_url} tam={62} style={{ margin: '0 auto' }} />
                        ) : (
                          <div
                            style={{
                              width: 62,
                              height: 62,
                              background: 'var(--grid-row-alt-bg)',
                              borderRadius: 4,
                              border: '1px solid var(--border)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <ImageOff size={16} color="#d1d5db" />
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--page-fg)' }}>{p.codigo_interno}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ fontWeight: 500, color: 'var(--page-fg)' }}>
                          {p.descricao}
                          {p.compra_em_aberto && (
                            <span
                              title={`Comprado ${p.ultima_compra_em ? formatDataSP(p.ultima_compra_em) : ''}${p.ultima_compra_fornecedor_nome ? ` · ${p.ultima_compra_fornecedor_nome}` : ''}${textoQtdComprada(p.compra_em_aberto_qtd, p.compra_em_aberto_emb)}`}
                              style={{ display: 'inline-block', marginLeft: 6, backgroundColor: '#fde68a', color: '#000', padding: '1px 6px', borderRadius: 9999, fontSize: 10, fontWeight: 700, verticalAlign: 'middle', whiteSpace: 'nowrap' }}
                            >
                              🛒 a caminho
                            </span>
                          )}
                        </div>
                        {p.cotacoes_abertas && p.cotacoes_abertas.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                            {p.cotacoes_abertas.map((num) => (
                              <span
                                key={num}
                                style={{
                                  display: 'inline-block',
                                  padding: '2px 6px',
                                  fontSize: 11,
                                  fontWeight: 700,
                                  borderRadius: 3,
                                  background: 'var(--primary)',
                                  color: 'var(--accent-fg)',
                                }}
                              >
                                {num}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--page-fg)' }}>{p.marca_nome || '—'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', color: 'var(--text-muted)' }}>{p.unidade_sigla || '—'}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--page-fg)' }}>{p.grupo_nome || '—'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 500, color: 'var(--page-fg)' }}>
                        {formatMoeda(p.preco_cartao)}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          textAlign: 'right',
                          fontWeight: 500,
                          color: estoqueAlerta ? '#dc2626' : 'var(--page-fg)',
                        }}
                      >
                        {Number(p.estoque_atual).toLocaleString('pt-BR')}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text-muted)' }}>
                        {Number(p.estoque_minimo).toLocaleString('pt-BR')}
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{p.localizacao || '—'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={(e) => abrirMenu(e, p)}
                          style={{ padding: 4, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
                          title="Acoes"
                        >
                          <MoreVertical size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>

      {/* Menu popup */}
      {menuAberto && (
        <div
          style={{
            position: 'fixed',
            background: 'var(--panel-bg)',
            borderRadius: 8,
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
            border: '1px solid var(--border)',
            padding: '4px 0',
            zIndex: 50,
            left: menuAberto.x,
            top: menuAberto.y,
            minWidth: 180,
          }}
        >
          <button
            type="button"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px',
              fontSize: 13,
              color: 'var(--page-fg)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
            onClick={() => {
              setModalCotacao({ produto_id: menuAberto.id, descricao: menuAberto.descricao });
              setMenuAberto(null);
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--primary-soft)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '')}
          >
            <Send size={14} /> Enviar para Cotacao
          </button>
        </div>
      )}

      {/* Modal selecionar cotacao */}
      {modalCotacao && (
        <ModalSelecionarCotacao
          produtoId={modalCotacao.produto_id}
          produtoDesc={modalCotacao.descricao}
          onClose={(enviou) => {
            setModalCotacao(null);
            if (enviou) carregar();
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// Modal Selecionar Cotacao
// ============================================================

interface ModalCotProps {
  produtoId: number;
  produtoDesc: string;
  onClose: (enviou: boolean) => void;
}

// Exportado: a tela Mais Vendidos usa o mesmo modal (mesma porta pra cotacao, nao uma segunda).
export function ModalSelecionarCotacao({ produtoId, produtoDesc, onClose }: ModalCotProps) {
  const toast = useToast();
  const [cotacoes, setCotacoes] = useState<CotacaoAberta[]>([]);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState<number | null>(null);
  const [enviado, setEnviado] = useState<number | null>(null);
  const [expandido, setExpandido] = useState<number | null>(null);
  const [itensCache, setItensCache] = useState<Record<number, ItemCotacao[]>>({});
  const [carregandoItens, setCarregandoItens] = useState<number | null>(null);

  useEffect(() => {
    api
      .get<CotacaoAberta[]>('/api/lista-compras/cotacoes-abertas')
      .then((r) => setCotacoes(r))
      .catch(() => setCotacoes([]))
      .finally(() => setLoading(false));
  }, []);

  async function toggleExpandir(cotId: number) {
    if (expandido === cotId) {
      setExpandido(null);
      return;
    }
    setExpandido(cotId);
    if (!itensCache[cotId]) {
      setCarregandoItens(cotId);
      try {
        const data = await api.get<ItemCotacao[]>(`/api/lista-compras/cotacao-itens/${cotId}`);
        setItensCache((prev) => ({ ...prev, [cotId]: data }));
      } catch {
        setItensCache((prev) => ({ ...prev, [cotId]: [] }));
      } finally {
        setCarregandoItens(null);
      }
    }
  }

  async function enviar(cotId: number) {
    setEnviando(cotId);
    try {
      await api.post('/api/lista-compras/enviar-cotacao', { cotacao_id: cotId, produto_id: produtoId });
      setEnviado(cotId);
      setItensCache((prev) => {
        const copy = { ...prev };
        delete copy[cotId];
        return copy;
      });
      setCotacoes((prev) =>
        prev.map((c) => (c.id === cotId ? { ...c, total_itens: String(Number(c.total_itens) + 1) } : c)),
      );
      setTimeout(() => onClose(true), 1200);
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao enviar');
    } finally {
      setEnviando(null);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 70,
      }}
    >
      <div
        style={{
          background: 'var(--modal-bg, var(--panel-bg, #fff))',
          color: 'var(--modal-fg, var(--panel-fg, #1f2937))',
          borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          width: '100%',
          maxWidth: 720,
          margin: 16,
        }}
      >
        <div className="modal-header" style={{ padding: '16px 20px' }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Enviar para Cotacao</h3>
          <button type="button" className="btn-fechar-modal" onClick={() => onClose(false)} aria-label="Fechar">×</button>
        </div>

        <div style={{ padding: '12px 20px', background: 'var(--grid-row-alt-bg)', borderBottom: '1px solid var(--border)', fontSize: 13, color: 'var(--page-fg)' }}>
          <span style={{ fontWeight: 500, color: 'var(--page-fg)' }}>{produtoDesc}</span>
        </div>

        <div style={{ padding: 20 }}>
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando cotacoes...</div>
          ) : cotacoes.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhuma cotacao aberta</div>
          ) : (
            <div style={{ maxHeight: 384, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {cotacoes.map((c) => {
                const isExp = expandido === c.id;
                const itens = itensCache[c.id];
                return (
                  <div
                    key={c.id}
                    style={{
                      borderRadius: 8,
                      border: '1px solid',
                      borderColor: enviado === c.id ? '#86efac' : 'var(--border)',
                      background: enviado === c.id ? '#f0fdf4' : 'var(--panel-bg)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px' }}>
                      <button
                        type="button"
                        onClick={() => toggleExpandir(c.id)}
                        style={{ padding: 4, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', flexShrink: 0 }}
                      >
                        {isExp ? <Minus size={14} /> : <Plus size={14} />}
                      </button>
                      <div style={{ width: 192, flexShrink: 0 }}>
                        <div style={{ fontWeight: 500, fontSize: 13, color: 'var(--page-fg)', whiteSpace: 'nowrap' }}>
                          Cotacao {c.numero}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {c.total_itens} iten{Number(c.total_itens) !== 1 ? 's' : ''}
                        </div>
                      </div>
                      <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--page-fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.observacoes || '—'}
                      </div>
                      {enviado === c.id ? (
                        <span style={{ fontSize: 11, color: '#15803d', fontWeight: 500, flexShrink: 0 }}>Enviado!</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => enviar(c.id)}
                          disabled={!!enviando || !!enviado}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 12px',
                            fontSize: 11,
                            fontWeight: 500,
                            color: 'var(--primary)',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: 8,
                            cursor: 'pointer',
                            flexShrink: 0,
                            opacity: enviando || enviado ? 0.4 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if (!enviando && !enviado) e.currentTarget.style.background = 'var(--primary-soft)';
                          }}
                          onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                        >
                          {enviando === c.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Enviar
                        </button>
                      )}
                    </div>
                    {isExp && (
                      <div style={{ borderTop: '1px solid var(--border)', padding: '8px 16px', background: 'var(--grid-row-alt-bg)', borderRadius: '0 0 8px 8px' }}>
                        {carregandoItens === c.id ? (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', padding: '8px 0', textAlign: 'center' }}>Carregando itens...</div>
                        ) : itens && itens.length > 0 ? (
                          <table style={{ width: '100%', fontSize: 11 }}>
                            <thead>
                              <tr style={{ color: 'var(--text-muted)' }}>
                                <th style={{ textAlign: 'left', padding: 4, fontWeight: 500 }}>Cod.</th>
                                <th style={{ textAlign: 'left', padding: 4, fontWeight: 500 }}>Descricao</th>
                                <th style={{ textAlign: 'center', padding: 4, fontWeight: 500, width: 40 }}>Qtd</th>
                                <th style={{ textAlign: 'center', padding: 4, fontWeight: 500, width: 40 }}>Un.</th>
                              </tr>
                            </thead>
                            <tbody>
                              {itens.map((it) => (
                                <tr key={it.id} style={{ borderTop: '1px solid var(--border)' }}>
                                  <td style={{ padding: 4, color: 'var(--text-muted)' }}>{it.codigo_interno}</td>
                                  <td style={{ padding: 4, color: 'var(--page-fg)' }}>{it.descricao}</td>
                                  <td style={{ padding: 4, textAlign: 'center', color: 'var(--page-fg)' }}>{it.quantidade}</td>
                                  <td style={{ padding: 4, textAlign: 'center', color: 'var(--text-muted)' }}>{it.unidade_sigla || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', padding: '8px 0', textAlign: 'center' }}>Nenhum item</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 20px', borderTop: '1px solid var(--border)' }}>
          <button
            type="button"
            onClick={() => onClose(false)}
            style={{ padding: '8px 16px', fontSize: 13, color: 'var(--page-fg)', background: 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer' }}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
